import React, { useCallback, useEffect, useRef, useState } from 'react';

function deviceCanHover() {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(hover: hover)').matches;
}

/**
 * Shows a still image; plays the video (muted, looping) while hovered.
 * On touch devices without hover, plays while mostly visible on screen instead.
 */
export default function HoverVideo({ src, poster, alt = '', className = '' }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const activeRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [canHover] = useState(deviceCanHover);

  const start = useCallback(() => {
    activeRef.current = true;
    const video = videoRef.current;
    if (!video) return;
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }, []);

  const stop = useCallback(() => {
    activeRef.current = false;
    setPlaying(false);
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // some browsers throw before metadata loads
    }
  }, []);

  useEffect(() => {
    if (canHover) return undefined;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) start();
        else stop();
      },
      { threshold: [0, 0.6] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canHover, start, stop]);

  const handlePlaying = () => {
    // play() can resolve after the pointer already left
    if (activeRef.current) setPlaying(true);
    else videoRef.current?.pause();
  };

  const classes = ['hover-video', poster ? 'hover-video--has-poster' : '', playing ? 'is-playing' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      ref={containerRef}
      className={classes}
      onMouseEnter={canHover ? start : undefined}
      onMouseLeave={canHover ? stop : undefined}
    >
      {poster && (
        <img className="hover-video__poster" src={poster} alt={alt} loading="lazy" draggable={false} />
      )}
      <video
        ref={videoRef}
        className="hover-video__video"
        src={src}
        muted
        loop
        playsInline
        preload={poster ? 'none' : 'metadata'}
        aria-label={alt || undefined}
        onPlaying={handlePlaying}
      />
    </span>
  );
}
